function _extends() { _extends = Object.assign || function (target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i]; for (var key in source) { if (Object.prototype.hasOwnProperty.call(source, key)) { target[key] = source[key]; } } } return target; }; return _extends.apply(this, arguments); }

/**
 * @param {options} options
 * @typedef {object} options
 * @property {boolean} isPlural Are we searching for 1 thing or many?
 * @property {string} queryName What the props come back on ( by default = modelName + 'Query')
 */
import { reduxForm } from "redux-form";
import { arrayMove } from "react-sortable-hoc";
import { toArray, keyBy, get } from "lodash";
import { withProps, withState, branch, compose } from "recompose";
import withTableParams from "../DataTable/utils/withTableParams";
import convertSchema from "../DataTable/utils/convertSchema";
import { viewColumn, openColumn } from "../DataTable/viewColumn";
import pureNoFunc from "../utils/pureNoFunc";
import tgFormValues from "../utils/tgFormValues";
import getTableConfigFromStorage from "./utils/getTableConfigFromStorage";
export default compose( //connect to withTableParams here in the dataTable component so that, in the case that the table is not manually connected,
withTableParams({
  isLocalCall: true
}), withState("showForcedHiddenColumns", "setShowForcedHidden", false), withProps(function (ownProps) {
  var propsToUse = ownProps;

  if (!ownProps.isTableParamsConnected) {
    //this is the case where we're hooking up to withTableParams locally, so we need to take the tableParams off the props
    propsToUse = _extends({}, ownProps, ownProps.tableParams);
  }

  var _propsToUse = propsToUse,
      schema = _propsToUse.schema,
      withDisplayOptions = _propsToUse.withDisplayOptions,
      syncDisplayOptionsToDb = _propsToUse.syncDisplayOptionsToDb,
      formName = _propsToUse.formName,
      tableConfigurations = _propsToUse.tableConfigurations,
      deleteTableConfiguration = _propsToUse.deleteTableConfiguration,
      upsertTableConfiguration = _propsToUse.upsertTableConfiguration,
      upsertFieldOption = _propsToUse.upsertFieldOption,
      currentUser = _propsToUse.currentUser,
      isViewable = _propsToUse.isViewable,
      isOpenable = _propsToUse.isOpenable,
      _propsToUse$entities = _propsToUse.entities,
      entities = _propsToUse$entities === void 0 ? [] : _propsToUse$entities,
      _propsToUse$cellRende = _propsToUse.cellRenderer,
      cellRenderer = _propsToUse$cellRende === void 0 ? {} : _propsToUse$cellRende,
      showForcedHiddenColumns = _propsToUse.showForcedHiddenColumns,
      isSimple = _propsToUse.isSimple,
      isInfinite = _propsToUse.isInfinite,
      _propsToUse$compact = _propsToUse.compact,
      compact = _propsToUse$compact === void 0 ? true : _propsToUse$compact,
      extraCompact = _propsToUse.extraCompact;
  var schemaToUse = convertSchema(schema);
  var fieldOptsByPath = {};
  var tableConfig = {};
  var resetDefaultVisibility;
  var updateColumnVisibility;
  var persistPageSize;
  var moveColumnPersist;
  var resizePersist;
  var updateTableDisplayDensity;
  var compactToUse = !!compact;
  var extraCompactToUse = !!extraCompact;

  if (isViewable) {
    schemaToUse.fields = [viewColumn].concat(schemaToUse.fields);
  }

  if (isOpenable) {
    schemaToUse.fields = [openColumn].concat(schemaToUse.fields);
  } // this must come before handling orderings.


  schemaToUse.fields = schemaToUse.fields.map(function (field) {
    if (field.placementPath) {
      return _extends({}, field, {
        sortDisabled: field.sortDisabled || typeof field.path === "string" && field.path.includes("."),
        path: field.placementPath
      });
    } else {
      return field;
    }
  });
  var hasOptionForForcedHidden = withDisplayOptions && (isSimple || isInfinite);

  if (withDisplayOptions) {
    if (syncDisplayOptionsToDb) {
      tableConfig = tableConfigurations && tableConfigurations[0];
    } else {
      tableConfig = getTableConfigFromStorage(formName);
    }

    if (!tableConfig) {
      tableConfig = {
        fieldOptions: []
      };
    }

    if (tableConfig.density) {
      compactToUse = tableConfig.density === "compact";
    }

    if (tableConfig.density) {
      extraCompactToUse = tableConfig.density === "extraCompact";
    }

    var columnOrderings = tableConfig.columnOrderings;
    fieldOptsByPath = keyBy(tableConfig.fieldOptions, "path");
    schemaToUse = _extends({}, schemaToUse, {
      fields: schemaToUse.fields.map(function (field) {
        var fieldOpt = fieldOptsByPath[field.path];
        var noValsForField = false; // only add this hidden column ability if no paging

        if (!showForcedHiddenColumns && hasOptionForForcedHidden) {
          noValsForField = entities.every(function (e) {
            var val = get(e, field.path);
            return field.render ? !field.render(val, e) : cellRenderer[field.path] ? !cellRenderer[field.path](val, e) : !val;
          });
        }

        if (noValsForField) {
          return _extends({}, field, {
            isHidden: true,
            isForcedHidden: true
          });
        } else if (fieldOpt) {
          return _extends({}, field, {
            isHidden: fieldOpt.isHidden
          });
        } else {
          return field;
        }
      })
    });

    if (columnOrderings) {
      var fieldsWithOrders = [];
      var fieldsWithoutOrder = []; // if a new field has been added since the orderings were set then we want
      // it to be at the end instead of the beginning

      schemaToUse.fields.forEach(function (field) {
        if (columnOrderings.indexOf(field.path) > -1) {
          fieldsWithOrders.push(field);
        } else {
          fieldsWithoutOrder.push(field);
        }
      });
      schemaToUse.fields = fieldsWithOrders.sort(function (_ref, _ref2) {
        var path1 = _ref.path;
        var path2 = _ref2.path;
        return columnOrderings.indexOf(path1) - columnOrderings.indexOf(path2);
      }).concat(fieldsWithoutOrder);
      tableConfig.columnOrderings = schemaToUse.fields.map(function (f) {
        return f.path;
      });
    }

    if (syncDisplayOptionsToDb) {
      //sync up to db
      var tableConfigurationId;

      resetDefaultVisibility = function resetDefaultVisibility() {
        tableConfigurationId = tableConfig.id;

        if (tableConfigurationId) {
          deleteTableConfiguration(tableConfigurationId);
        }
      };

      updateColumnVisibility = function updateColumnVisibility(_ref3) {
        var shouldShow = _ref3.shouldShow,
            path = _ref3.path;

        if (tableConfigurationId) {
          var existingFieldOpt = fieldOptsByPath[path] || {};
          upsertFieldOption({
            id: existingFieldOpt.id,
            path: path,
            isHidden: !shouldShow,
            tableConfigurationId: tableConfigurationId
          });
        } else {
          upsertTableConfiguration({
            userId: currentUser.user.id,
            formName: formName,
            fieldOptions: [{
              path: path,
              isHidden: !shouldShow
            }]
          });
        }
      };
    } else {
      var syncStorage = function syncStorage() {
        window.localStorage.setItem(formName, JSON.stringify(tableConfig));
      }; //sync display options with localstorage


      resetDefaultVisibility = function resetDefaultVisibility() {
        window.localStorage.removeItem(formName);
      };

      updateColumnVisibility = function updateColumnVisibility(_ref4) {
        var path = _ref4.path,
            paths = _ref4.paths,
            shouldShow = _ref4.shouldShow;

        var newFieldOpts = _extends({}, fieldOptsByPath);

        var pathsToUse = paths ? paths : [path];
        pathsToUse.forEach(function (path) {
          newFieldOpts[path] = {
            path: path,
            isHidden: !shouldShow
          };
        });
        tableConfig.fieldOptions = toArray(newFieldOpts);
        syncStorage();
      };

      updateTableDisplayDensity = function updateTableDisplayDensity(density) {
        tableConfig.density = density;
        syncStorage();
      };

      persistPageSize = function persistPageSize(pageSize) {
        tableConfig.userSetPageSize = pageSize;
        syncStorage();
      };

      moveColumnPersist = function moveColumnPersist(_ref5) {
        var oldIndex = _ref5.oldIndex,
            newIndex = _ref5.newIndex;
        // we might already have an array of the fields [path1, path2, ..etc]
        var columnOrderings = tableConfig.columnOrderings || schemaToUse.fields.map(function (_ref6) {
          var path = _ref6.path;
          return path;
        }); // columnOrderings is [path1, path2, ..etc]

        tableConfig.columnOrderings = arrayMove(columnOrderings, oldIndex, newIndex);
        syncStorage();
      };

      resizePersist = function resizePersist(newResized) {
        tableConfig.resized = newResized;
        syncStorage();
      };
    }
  }

  var resized = tableConfig.resized;
  return _extends({}, propsToUse, {
    schema: schemaToUse,
    compact: compactToUse,
    extraCompact: extraCompactToUse,
    resized: resized,
    resetDefaultVisibility: resetDefaultVisibility,
    updateColumnVisibility: updateColumnVisibility,
    persistPageSize: persistPageSize,
    updateTableDisplayDensity: updateTableDisplayDensity,
    resizePersist: resizePersist,
    moveColumnPersist: moveColumnPersist,
    hasOptionForForcedHidden: hasOptionForForcedHidden
  });
}), branch(function (props) {
  return !props.noForm;
}, reduxForm({})), //the formName is passed via withTableParams and is often user overridden
tgFormValues("localStorageForceUpdate", "reduxFormQueryParams", "reduxFormSearchInput", "reduxFormSelectedEntityIdMap", "reduxFormExpandedEntityIdMap", "reduxFormSelectedCells", "reduxFormEditingCell", "reduxFormCellIdToEditValue", "reduxFormEntities", "reduxFormCellValidation", "reduxFormEntitiesUndoRedoStack"), withProps(function (props) {
  var entities = props.reduxFormEntities || props.entities;
  return {
    _origEntities: props.entities,
    entities: entities
  };
}), // withFields({
//   names: [
//     "localStorageForceUpdate",
//     "reduxFormQueryParams",
//     "reduxFormSearchInput",
//     "reduxFormSelectedEntityIdMap",
//     "reduxFormExpandedEntityIdMap"
//   ]
// }),
branch(function (props) {
  return !props.alwaysRerender;
}, pureNoFunc));